import { useEffect, useMemo, useState } from "react";
import { http, fmtINR, fmtNum } from "@/lib/api";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const SOURCE_COLORS = {
    POS: "bg-blue-500/10 text-blue-600 border-blue-500/30",
    CSV: "bg-purple-500/10 text-purple-600 border-purple-500/30",
    API: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
    MOBILE: "bg-amber-500/10 text-amber-600 border-amber-500/30",
    MANUAL: "bg-slate-500/10 text-slate-600 border-slate-500/30",
};

export default function SellOut() {
    const [recent, setRecent] = useState([]);
    const [trend, setTrend] = useState([]);
    const [source, setSource] = useState("All");

    useEffect(() => {
        http.get("/sellout/recent", { params: { limit: 400 } }).then((r) => setRecent(r.data));
        http.get("/sellout/trend", { params: { days: 60 } }).then((r) => setTrend(r.data));
    }, []);

    const filtered = useMemo(
        () => (source === "All" ? recent : recent.filter((r) => r.source === source)),
        [recent, source]
    );

    const sourceMix = useMemo(() => {
        const m = {};
        recent.forEach((r) => (m[r.source] = (m[r.source] || 0) + 1));
        return Object.entries(m).map(([k, v]) => ({ source: k, count: v }));
    }, [recent]);

    return (
        <div className="mx-auto max-w-[1560px] space-y-4 p-6">
            <div className="flex items-end justify-between">
                <div>
                    <div className="mlabel">Sell-Out Data Hub</div>
                    <h1 className="text-[22px] font-semibold tracking-tight">Multi-source Sell-Out Ingestion</h1>
                </div>
                <div className="text-[11px] text-muted-foreground">
                    Last {recent.length} records across POS · CSV · API · Manual
                </div>
            </div>

            <div className="grid grid-cols-12 gap-3">
                <div className="col-span-12 rounded-sm border border-border bg-card p-4 lg:col-span-8">
                    <div className="mlabel mb-3">Daily Sell-Out · 60 days</div>
                    <div className="h-[220px]" data-testid="chart-sellout-60">
                        <ResponsiveContainer>
                            <AreaChart data={trend}>
                                <defs>
                                    <linearGradient id="so60" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="hsl(var(--chart-1))" stopOpacity={0.35} />
                                        <stop offset="100%" stopColor="hsl(var(--chart-1))" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" vertical={false} />
                                <XAxis
                                    dataKey="date"
                                    tickFormatter={(d) => d.slice(5)}
                                    stroke="hsl(var(--muted-foreground))"
                                    fontSize={10}
                                    tickLine={false}
                                />
                                <YAxis
                                    stroke="hsl(var(--muted-foreground))"
                                    fontSize={10}
                                    tickFormatter={(v) => (v >= 1e5 ? `${(v / 1e5).toFixed(1)}L` : v)}
                                    width={40}
                                    tickLine={false}
                                />
                                <Tooltip
                                    contentStyle={{
                                        background: "hsl(var(--card))",
                                        border: "1px solid hsl(var(--border))",
                                        fontSize: 12,
                                    }}
                                    formatter={(v) => fmtINR(v)}
                                />
                                <Area
                                    type="monotone"
                                    dataKey="value"
                                    stroke="hsl(var(--chart-1))"
                                    strokeWidth={2}
                                    fill="url(#so60)"
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="col-span-12 rounded-sm border border-border bg-card p-4 lg:col-span-4">
                    <div className="mlabel mb-3">Source Mix</div>
                    <div className="space-y-2" data-testid="source-mix">
                        {sourceMix.map((s) => (
                            <div key={s.source} className="flex items-center justify-between">
                                <span
                                    className={
                                        "rounded-sm border px-1.5 py-[1px] text-[10px] font-medium " +
                                        SOURCE_COLORS[s.source]
                                    }
                                >
                                    {s.source}
                                </span>
                                <span className="mono-num text-[12px]">{fmtNum(s.count)}</span>
                            </div>
                        ))}
                    </div>
                    <div className="mt-4 rounded-sm border border-dashed border-border p-3 text-[11px] leading-relaxed text-muted-foreground">
                        Data lineage & anomaly checks run automatically on every source. Exceptions surface as alerts.
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-1 rounded-sm border border-border bg-card p-0.5">
                {["All", "POS", "CSV", "API", "MOBILE", "MANUAL"].map((s) => (
                    <button
                        key={s}
                        data-testid={`source-filter-${s}`}
                        onClick={() => setSource(s)}
                        className={
                            "rounded-sm px-2.5 py-1 text-[11px] font-medium transition-colors " +
                            (source === s
                                ? "bg-primary text-primary-foreground"
                                : "text-muted-foreground hover:text-foreground")
                        }
                    >
                        {s}
                    </button>
                ))}
                <div className="ml-auto pr-2 text-[11px] text-muted-foreground">
                    {fmtNum(filtered.length)} records
                </div>
            </div>

            <div className="overflow-hidden rounded-sm border border-border bg-card" data-testid="sellout-table">
                <div className="max-h-[calc(100vh-320px)] overflow-auto">
                    <table className="w-full text-[12px]">
                        <thead className="sticky top-0 z-10 bg-muted/60 text-[10px] uppercase tracking-wider text-muted-foreground">
                            <tr>
                                <th className="px-3 py-2 text-left font-semibold">Date</th>
                                <th className="px-3 py-2 text-left font-semibold">Store</th>
                                <th className="px-3 py-2 text-left font-semibold">SKU</th>
                                <th className="px-3 py-2 text-left font-semibold">Product</th>
                                <th className="px-3 py-2 text-right font-semibold">Qty</th>
                                <th className="px-3 py-2 text-right font-semibold">Value</th>
                                <th className="px-3 py-2 text-left font-semibold">Source</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.slice(0, 300).map((r) => (
                                <tr
                                    key={r.id}
                                    data-testid={`sellout-row-${r.id}`}
                                    className="border-t border-border hover:bg-muted/40"
                                >
                                    <td className="mono-num px-3 py-1.5 text-muted-foreground">
                                        {r.txn_date?.slice(0, 10)}
                                    </td>
                                    <td className="px-3 py-1.5">
                                        {r.store?.name}
                                        <span className="ml-1 text-muted-foreground">· {r.store?.city}</span>
                                    </td>
                                    <td className="mono-num px-3 py-1.5">{r.product?.sku}</td>
                                    <td className="px-3 py-1.5">{r.product?.name}</td>
                                    <td className="mono-num px-3 py-1.5 text-right">{r.qty}</td>
                                    <td className="mono-num px-3 py-1.5 text-right font-medium">{fmtINR(r.value)}</td>
                                    <td className="px-3 py-1.5">
                                        <span
                                            className={
                                                "rounded-sm border px-1.5 py-[1px] text-[10px] font-medium " +
                                                SOURCE_COLORS[r.source]
                                            }
                                        >
                                            {r.source}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
